import React, { useState, useEffect } from 'react';
import { createWithdrawalRequest, getUserWithdrawals } from '../../api/withdrawalsApi';
import { toast } from 'react-toastify';
import { supabase, API_BASE } from '../../App'; // Fixed import path

function WithdrawalRequest() {
  const [balance, setBalance] = useState(0);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('upi'); // 'upi' or 'bank'
  
  // UPI Details
  const [upiId, setUpiId] = useState('');
  
  // Bank Details
  const [bankDetails, setBankDetails] = useState({
    accountName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: ''
  });
  
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchWalletBalance();
    fetchWithdrawals();
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_BASE}/wallet/me`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setBalance(data.balance_bigint || 0);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const data = await getUserWithdrawals(session.access_token);
      setWithdrawals(data || []);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const withdrawAmount = parseFloat(amount);
    
    // Validation
    if (!withdrawAmount || withdrawAmount < 100) {
      toast.error('Minimum withdrawal amount is ₹100');
      return;
    }

    if (withdrawAmount > balance / 100) {
      toast.error('Insufficient balance');
      return;
    }

    // Method-specific validation
    if (method === 'upi') {
      if (!upiId || !upiId.includes('@')) {
        toast.error('Please enter a valid UPI ID (e.g., username@upi)');
        return;
      }
    } else {
      if (!bankDetails.accountName || !bankDetails.accountNumber || !bankDetails.ifscCode) {
        toast.error('Please fill in all required bank details');
        return;
      }
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please login again');
        return;
      }

      // Prepare payload based on method
      const payload = {
        amountBigint: Math.round(withdrawAmount * 100), // Convert to paise
        method: method,
        details: method === 'upi' 
          ? {
              fund_account: {
                account_type: 'vpa',
                vpa: {
                  address: upiId
                }
              }
            }
          : {
              fund_account: {
                account_type: 'bank_account',
                bank_account: {
                  name: bankDetails.accountName,
                  ifsc: bankDetails.ifscCode,
                  account_number: bankDetails.accountNumber
                }
              }
            }
      };

      await createWithdrawalRequest(payload, session.access_token);
      
      toast.success(`Withdrawal request submitted! 💰 Your ${method.toUpperCase()} withdrawal will be processed within 24-48 hours.`);
      
      // Reset form
      setShowForm(false);
      setAmount('');
      setUpiId('');
      setBankDetails({
        accountName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: ''
      });
      
      fetchWalletBalance();
      fetchWithdrawals();
    } catch (error) {
      console.error('Withdrawal error:', error);
      toast.error(error.response?.data?.error || 'Failed to submit withdrawal request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      requested: { label: 'Pending Review', color: '#FFA500', icon: '⏳' },
      approved: { label: 'Approved', color: '#00CED1', icon: '✓' },
      processed: { label: 'Completed', color: '#00FF00', icon: '✅' },
      rejected: { label: 'Rejected', color: '#FF4444', icon: '✗' }
    };

    const config = statusConfig[status] || statusConfig.requested;
    
    return (
      <span style={{
        background: `${config.color}22`,
        color: config.color,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        border: `1px solid ${config.color}44`
      }}>
        {config.icon} {config.label}
      </span>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          fontSize: '2rem', 
          marginBottom: '10px',
          fontFamily: 'Orbitron, sans-serif',
          background: 'linear-gradient(135deg, #00f0ff, #ff00ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          💸 Withdrawal Requests
        </h2>
        <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Withdraw your winnings via UPI or Bank Transfer
        </p>
      </div>

      {/* Balance Card */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(255, 0, 255, 0.1))',
        border: '2px solid #00f0ff',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '30px'
      }}>
        <div style={{ marginBottom: '15px', color: 'rgba(255, 255, 255, 0.7)' }}>
          💰 Available Balance
        </div>
        <div style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          fontFamily: 'Orbitron, sans-serif',
          background: 'linear-gradient(135deg, #00f0ff, #ff00ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '20px'
        }}>
          ₹{(balance / 100).toFixed(2)}
        </div>
        <button 
          style={{
            background: balance >= 10000 
              ? 'linear-gradient(135deg, #00f0ff, #ff00ff)'
              : 'rgba(255, 255, 255, 0.1)',
            color: balance >= 10000 ? '#000' : 'rgba(255, 255, 255, 0.5)',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: balance >= 10000 ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s ease'
          }}
          onClick={() => setShowForm(!showForm)}
          disabled={balance < 10000}
        >
          {showForm ? '❌ Cancel' : '💸 Request Withdrawal'}
        </button>
        {balance < 10000 && (
          <p style={{ marginTop: '10px', color: '#FFA500', fontSize: '0.9rem' }}>
            ⚠️ Minimum withdrawal amount is ₹100
          </p>
        )}
      </div>

      {/* Withdrawal Form */}
      {showForm && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '30px',
          marginBottom: '30px'
        }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '20px', color: '#00f0ff' }}>
            📝 New Withdrawal Request
          </h3>
          
          <form onSubmit={handleSubmit}>
            {/* Amount Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 'bold'
              }}>
                💵 Withdrawal Amount (₹)
              </label>
              <input
                type="number"
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  padding: '12px',
                  color: '#fff',
                  fontSize: '1.1rem'
                }}
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="100"
                max={balance / 100}
                required
              />
              <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '5px' }}>
                Min: ₹100 | Max: ₹{(balance / 100).toFixed(2)}
              </p>
            </div>

            {/* Payment Method Tabs */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '12px', 
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 'bold'
              }}>
                💳 Payment Method
              </label>
              <div style={{ display: 'flex', gap: '15px' }}>
                <button
                  type="button"
                  onClick={() => setMethod('upi')}
                  style={{
                    flex: 1,
                    background: method === 'upi' 
                      ? 'linear-gradient(135deg, #00f0ff, #ff00ff)'
                      : 'rgba(255, 255, 255, 0.05)',
                    color: method === 'upi' ? '#000' : '#fff',
                    border: method === 'upi' 
                      ? 'none'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '15px',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📱 UPI
                </button>
                <button
                  type="button"
                  onClick={() => setMethod('bank')}
                  style={{
                    flex: 1,
                    background: method === 'bank' 
                      ? 'linear-gradient(135deg, #00f0ff, #ff00ff)'
                      : 'rgba(255, 255, 255, 0.05)',
                    color: method === 'bank' ? '#000' : '#fff',
                    border: method === 'bank' 
                      ? 'none'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '15px',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  🏦 Bank Transfer
                </button>
              </div>
            </div>

            {/* UPI Form */}
            {method === 'upi' && (
              <div style={{
                background: 'rgba(0, 240, 255, 0.05)',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h4 style={{ fontSize: '1.2rem', marginBottom: '15px', color: '#00f0ff' }}>
                  📱 UPI Details
                </h4>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    UPI ID *
                  </label>
                  <input
                    type="text"
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                    placeholder="username@upi"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    required
                  />
                  <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '5px' }}>
                    Examples: yourname@paytm, yourname@googleplay, yourname@phonepe
                  </p>
                </div>
              </div>
            )}

            {/* Bank Form */}
            {method === 'bank' && (
              <div style={{
                background: 'rgba(0, 240, 255, 0.05)',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h4 style={{ fontSize: '1.2rem', marginBottom: '15px', color: '#00f0ff' }}>
                  🏦 Bank Account Details
                </h4>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    Account Holder Name *
                  </label>
                  <input
                    type="text"
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                    placeholder="As per bank records"
                    value={bankDetails.accountName}
                    onChange={(e) => setBankDetails({...bankDetails, accountName: e.target.value})}
                    required
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    Account Number *
                  </label>
                  <input
                    type="text"
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontFamily: 'monospace'
                    }}
                    placeholder="Enter account number"
                    value={bankDetails.accountNumber}
                    onChange={(e) => setBankDetails({...bankDetails, accountNumber: e.target.value})}
                    required
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    IFSC Code *
                  </label>
                  <input
                    type="text"
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: '#fff',
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      fontFamily: 'monospace'
                    }}
                    placeholder="e.g., SBIN0001234"
                    value={bankDetails.ifscCode}
                    onChange={(e) => setBankDetails({...bankDetails, ifscCode: e.target.value.toUpperCase()})}
                    maxLength="11"
                    required
                  />
                </div>

                <div style={{ marginBottom: '0' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    Bank Name (Optional)
                  </label>
                  <input
                    type="text"
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                    placeholder="e.g., State Bank of India"
                    value={bankDetails.bankName}
                    onChange={(e) => setBankDetails({...bankDetails, bankName: e.target.value})}
                  />
                </div>
              </div>
            )}

            {/* Warning Box */}
            <div style={{
              background: 'rgba(255, 170, 0, 0.1)',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid rgba(255, 170, 0, 0.3)'
            }}>
              <p style={{ fontSize: '0.9rem', color: '#FFA500', margin: 0 }}>
                ⚠️ <strong>Important:</strong> Withdrawal requests are processed within 24-48 hours. 
                Please ensure your {method.toUpperCase()} details are correct to avoid delays.
              </p>
            </div>

            {/* Submit Button */}
            <button 
              type="submit"
              style={{
                width: '100%',
                background: loading 
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'linear-gradient(135deg, #00FF00, #00AA00)',
                color: loading ? 'rgba(255, 255, 255, 0.5)' : '#fff',
                border: 'none',
                padding: '15px',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
              disabled={loading}
            >
              {loading ? '⏳ Submitting...' : '✅ Submit Withdrawal Request'}
            </button>
          </form>
        </div>
      )}

      {/* Withdrawal History */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '30px'
      }}>
        <h3 style={{ fontSize: '1.5rem', marginBottom: '20px', color: '#00f0ff' }}>
          📋 Withdrawal History
        </h3>

        {withdrawals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.5)' }}>
            <p style={{ fontSize: '3rem', marginBottom: '15px' }}>💸</p>
            <p>No withdrawal requests yet</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: '0 10px'
            }}>
              <thead>
                <tr style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  <th style={{ textAlign: 'left', padding: '10px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '10px' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '10px' }}>Method</th>
                  <th style={{ textAlign: 'left', padding: '10px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '10px' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((withdrawal) => (
                  <tr key={withdrawal.id} style={{
                    background: 'rgba(255, 255, 255, 0.03)'
                  }}>
                    <td style={{ padding: '15px', borderRadius: '8px 0 0 8px' }}>
                      {new Date(withdrawal.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '15px', fontWeight: 'bold', color: '#00FF00' }}>
                      ₹{(withdrawal.amount_bigint / 100).toFixed(2)}
                    </td>
                    <td style={{ padding: '15px' }}>
                      <span style={{
                        background: 'rgba(0, 240, 255, 0.2)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.85rem'
                      }}>
                        {withdrawal.method === 'upi' ? '📱 UPI' : '🏦 Bank'}
                      </span>
                    </td>
                    <td style={{ padding: '15px' }}>
                      {getStatusBadge(withdrawal.status)}
                    </td>
                    <td style={{ padding: '15px', fontSize: '0.85rem', fontFamily: 'monospace', borderRadius: '0 8px 8px 0' }}>
                      {withdrawal.method === 'upi' 
                        ? withdrawal.details?.fund_account?.vpa?.address || '-'
                        : `****${withdrawal.details?.fund_account?.bank_account?.account_number?.slice(-4) || '****'}`
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WithdrawalRequest;